import React, { PropsWithChildren, forwardRef, useCallback, useImperativeHandle, useReducer, useRef } from 'react'
import cn from 'clsx'
import ReactModal from 'react-modal'
import { DRAWER_HEADER_SUBTITLE_ID, DRAWER_HEADER_TITLE_ID } from './DrawerHeader'
import { drawerBehaviourStateReducer } from './context/DrawerBehaviourState'
import { DrawerBehaviourDispatchContext, DrawerBehaviourStateContext } from './context/DrawerBehaviourContexts'
import { REACT_MODAL_TRANSITION_DURATION } from 'styles/reactModal'

export const DRAWER_BASE_SIZES = ['small', 'large'] as const
export const DRAWER_BASE_DEFAULT_SIZE = DRAWER_BASE_SIZES[0]

export interface DrawerBaseProps {
  className?: string
  isOpen: boolean
  size?: typeof DRAWER_BASE_SIZES[number]
  onAfterClose?: () => void
  onClose?: () => void
}

const DrawerBase = forwardRef<HTMLDivElement, PropsWithChildren<DrawerBaseProps>>((props, ref) => {
  const {
    children,
    className,
    isOpen,
    size,
    onAfterClose,
    onClose,
  } = props

  const [state, dispatch] = useReducer(drawerBehaviourStateReducer, {})

  const baseRef = useRef<HTMLDivElement | null>(null)

  useImperativeHandle(ref, () => baseRef.current!)

  const handleClose = useCallback((event) => {
    event.stopPropagation()
    onClose?.()
  }, [onClose])

  return <ReactModal
    aria={{
      labelledby: DRAWER_HEADER_TITLE_ID,
      describedby: DRAWER_HEADER_SUBTITLE_ID,
    }}
    overlayClassName="drawer__overlay"
    className={cn(
      className,
      'drawer__content',
      `is-${size}`,
      { 'is-open': isOpen },
    )}
    closeTimeoutMS={REACT_MODAL_TRANSITION_DURATION}
    contentRef={(node) => { baseRef.current = node }}
    isOpen={isOpen}
    shouldCloseOnEsc
    shouldReturnFocusAfterClose={false}
    onAfterClose={onAfterClose}
    onRequestClose={handleClose}
  >
    <DrawerBehaviourStateContext.Provider value={state}>
      <DrawerBehaviourDispatchContext.Provider value={dispatch}>
        {children}
      </DrawerBehaviourDispatchContext.Provider>
    </DrawerBehaviourStateContext.Provider>
  </ReactModal>
})

DrawerBase.defaultProps = {
  size: DRAWER_BASE_DEFAULT_SIZE,
}

export default DrawerBase
